/**
 * LabelBatchTray — floating fixed-bottom widget that shows pending label
 * batch items and prints them all to one PDF.
 *
 * Mounted once globally in App.js so it appears whenever items > 0.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Printer, X, ListChecks, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useLabelBatch } from '@/lib/useLabelBatch';
import { renderLabelsPdf } from '@/components/PrintLabelButton';

const LabelBatchTray = () => {
  const items = useLabelBatch(s => s.items);
  const remove = useLabelBatch(s => s.remove);
  const clear = useLabelBatch(s => s.clear);
  const [layout, setLayout] = useState('avery_5160');
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  if (items.length === 0) return null;

  // Group by entity so we can issue one request per entity type
  const byEntity = items.reduce((acc, x) => {
    (acc[x.entity] ||= []).push(x);
    return acc;
  }, {});

  const printAll = async () => {
    setBusy(true);
    try {
      for (const [entity, list] of Object.entries(byEntity)) {
        await renderLabelsPdf({
          entity, ids: list.map(x => x.id),
          layout, copies: 1,
          includeName: true, includeCode: true,
        });
      }
      toast.success(`Printed ${items.length} labels`);
      clear();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Print failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-full max-w-[360px] bg-slate-900/95 border border-slate-700 rounded-xl shadow-2xl backdrop-blur"
      data-testid="label-batch-tray"
    >
      <div className="flex items-center justify-between p-3 border-b border-slate-800">
        <span className="text-sm font-semibold text-slate-100 inline-flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-indigo-300" />
          Label batch
          <Badge className="bg-indigo-500/20 border-indigo-400/40 text-indigo-200 border ml-1">
            {items.length}
          </Badge>
        </span>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                   onClick={() => setExpanded(v => !v)}
                   data-testid="label-batch-toggle">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                   onClick={clear} title="Clear all"
                   data-testid="label-batch-clear">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="max-h-[300px] overflow-y-auto p-2 space-y-1 border-b border-slate-800">
          {items.map(x => (
            <div key={`${x.entity}-${x.id}`}
                 className="flex items-center gap-2 bg-slate-800/40 border border-slate-800 rounded-md px-2 py-1.5 text-xs"
                 data-testid={`label-batch-item-${x.id}`}>
              <Badge variant="outline" className="text-[10px] capitalize">{x.entity}</Badge>
              <span className="flex-1 truncate text-slate-200">{x.name || x.id}</span>
              <Button size="sm" variant="ghost" className="h-5 w-5 p-0"
                       onClick={() => remove(x.id)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="p-3 space-y-2">
        <Select value={layout} onValueChange={setLayout}>
          <SelectTrigger className="bg-slate-900 border-slate-800 h-8 text-xs"
                          data-testid="label-batch-layout">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="avery_5160">Avery 5160 (sheet, 30/page)</SelectItem>
            <SelectItem value="avery_5163">Avery 5163 (sheet, 10/page)</SelectItem>
            <SelectItem value="brother_dk_22205">Brother DK-22205 (62mm continuous)</SelectItem>
            <SelectItem value="dymo_30252">Dymo 30252 (89×28mm address)</SelectItem>
          </SelectContent>
        </Select>
        <Button className="w-full" onClick={printAll} disabled={busy}
                 data-testid="label-batch-print">
          <Printer className="h-4 w-4 mr-1" />
          {busy ? 'Generating…' : `Print ${items.length} labels`}
        </Button>
      </div>
    </div>
  );
};

export default LabelBatchTray;
